/* eslint-disable react/prop-types */
import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "../../utils";
import { cva, VariantProps } from "class-variance-authority";
import { buttonVariants } from "./Button";

const tabsVariants = cva(undefined, {
  variants: {
    variant: {
      default: undefined,
      navigation: undefined,
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const tabsListVariants = cva(undefined, {
  variants: {
    variant: {
      default:
        "text-muted-foreground inline-flex h-9 items-center justify-center bg-transparent p-1",
      navigation:
        "inline-flex w-fit overflow-x-auto whitespace-nowrap rounded-lg border border-gray-200 bg-gray-100 p-1 md:flex",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const tabsTriggerVariants = cva(undefined, {
  variants: {
    variant: {
      default:
        "data-[state=active]:border-b-primary-500 ring-offset-background focus-visible:ring-ring data-[state=active]:text-primary-500 inline-flex items-center justify-center whitespace-nowrap px-3 py-1 text-sm font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border-b-2",
      navigation: cn(
        buttonVariants({ variant: "ghost" }),
        "data-[state=active]:bg-white data-[state=active]:font-extrabold data-[state=active]:text-primary-500",
      ),
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

type TabsVariantContextProps = VariantProps<typeof tabsVariants>;

const TabsVariantContext = React.createContext<TabsVariantContextProps>({});

const TabsVariantProvider = ({
  children,
  ...variants
}: React.PropsWithChildren<TabsVariantContextProps>) => (
  <TabsVariantContext.Provider value={variants}>
    {children}
  </TabsVariantContext.Provider>
);

const useTabsVariant = () => {
  const context = React.useContext(TabsVariantContext);
  if (context === undefined) {
    throw new Error("useTabsVariant must be used within a TabsProvider");
  }
  return context;
};

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> &
    VariantProps<typeof tabsVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <TabsVariantProvider variant={variant}>
      <TabsPrimitive.Root
        ref={ref}
        className={tabsVariants({ variant, className })}
        {...props}
      />
    </TabsVariantProvider>
  );
});
Tabs.displayName = TabsPrimitive.List.displayName;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const { variant } = useTabsVariant();
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const { variant } = useTabsVariant();
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={tabsTriggerVariants({
        variant,
        className,
      })}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "ring-offset-background focus-visible:ring-ring mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
